import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';

import Button from '@/components/Button';
import Stack from '@/components/Stack';
import Pagination from '@/components/Pagination';
import { fetchZipcodes, bulkAddZipcodes, bulkRemoveZipcodes } from './api';
import type { RegionZipcode, Pagination as PaginationType } from './types';

type Props = {
  regionId: number;
  regionState: string;
  onZipcodesChanged: () => void;
};

const ZipcodeManager = ({
  regionId,
  regionState,
  onZipcodesChanged,
}: Props) => {
  const [tab, setTab] = useState(0);

  // Current zipcodes state
  const [zipcodes, setZipcodes] = useState<RegionZipcode[]>([]);
  const [pagination, setPagination] = useState<PaginationType | null>(null);
  const [zipPage, setZipPage] = useState(1);
  const [zipSearch, setZipSearch] = useState('');
  const [selectedZips, setSelectedZips] = useState<Set<string>>(new Set());
  const [zipLoading, setZipLoading] = useState(false);

  // Add zipcodes state
  const [manualZips, setManualZips] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addResult, setAddResult] = useState<{
    added: number;
    skipped: number;
    conflicts: Array<{ zip: string; city: string; existing_region: string }>;
  } | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  // Remove state
  const [removeLoading, setRemoveLoading] = useState(false);

  const loadZipcodes = useCallback(async () => {
    setZipLoading(true);
    try {
      const response = await fetchZipcodes(regionId, {
        page: zipPage,
        per_page: 100,
        search: zipSearch || undefined,
      });
      setZipcodes(response.data);
      setPagination(response.pagination);
    } catch (err: any) {
      console.error('Failed to load zipcodes:', err);
    } finally {
      setZipLoading(false);
    }
  }, [regionId, zipPage, zipSearch]);

  useEffect(() => {
    loadZipcodes();
  }, [loadZipcodes]);

  const handleStateFill = async () => {
    setAddLoading(true);
    setAddError(null);
    setAddResult(null);

    try {
      const response = await bulkAddZipcodes(regionId, { mode: 'state' });
      setAddResult(response.data);
      onZipcodesChanged();
      loadZipcodes();
    } catch (err: any) {
      setAddError(
        err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to add zip codes',
      );
    } finally {
      setAddLoading(false);
    }
  };

  const handleManualAdd = async () => {
    const zips = manualZips
      .split(/[\s,]+/)
      .map((z) => z.trim())
      .filter((z) => z.length > 0);

    if (zips.length === 0) {
      setAddError('Enter at least one zip code');
      return;
    }

    setAddLoading(true);
    setAddError(null);
    setAddResult(null);

    try {
      const response = await bulkAddZipcodes(regionId, {
        mode: 'manual',
        zipcodes: zips,
      });
      setAddResult(response.data);
      setManualZips('');
      onZipcodesChanged();
      loadZipcodes();
    } catch (err: any) {
      setAddError(
        err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to add zip codes',
      );
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveSelected = async () => {
    if (selectedZips.size === 0) return;

    setRemoveLoading(true);
    try {
      await bulkRemoveZipcodes(regionId, Array.from(selectedZips));
      setSelectedZips(new Set());
      onZipcodesChanged();
      loadZipcodes();
    } catch (err: any) {
      console.error('Failed to remove zipcodes:', err);
    } finally {
      setRemoveLoading(false);
    }
  };

  const toggleZip = (zip: string) => {
    setSelectedZips((prev) => {
      const next = new Set(prev);
      if (next.has(zip)) {
        next.delete(zip);
      } else {
        next.add(zip);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedZips.size === zipcodes.length) {
      setSelectedZips(new Set());
    } else {
      setSelectedZips(new Set(zipcodes.map((z) => z.zip)));
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Zip Codes
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Current" />
        <Tab label="Add Zip Codes" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Stack align="center" justify="apart">
            <TextField
              size="small"
              placeholder="Search by zip or city..."
              value={zipSearch}
              onChange={(e) => {
                setZipSearch(e.target.value);
                setZipPage(1);
              }}
              sx={{ width: 250 }}
            />
            {selectedZips.size > 0 && (
              <Button
                a11yLabel="Remove selected zip codes"
                label={`Remove ${selectedZips.size} selected`}
                status="danger"
                isLoading={removeLoading}
                onClick={handleRemoveSelected}
              />
            )}
          </Stack>

          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        zipcodes.length > 0 &&
                        selectedZips.size === zipcodes.length
                      }
                      indeterminate={
                        selectedZips.size > 0 &&
                        selectedZips.size < zipcodes.length
                      }
                      onChange={toggleAll}
                    />
                  </TableCell>
                  <TableCell>Zip Code</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>State</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {zipLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : zipcodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No zip codes assigned to this region
                    </TableCell>
                  </TableRow>
                ) : (
                  zipcodes.map((z) => (
                    <TableRow key={z.zip} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedZips.has(z.zip)}
                          onChange={() => toggleZip(z.zip)}
                        />
                      </TableCell>
                      <TableCell>{z.zip}</TableCell>
                      <TableCell>{z.city}</TableCell>
                      <TableCell>{z.state}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination && pagination.total_pages > 1 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                currentPage={zipPage}
                totalPages={pagination.total_pages}
                onPageChange={setZipPage}
              />
            </Box>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          {addError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addError}
            </Alert>
          )}

          {addResult && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Added {addResult.added} zip codes.
              {addResult.skipped > 0 &&
                ` Skipped ${addResult.skipped} (already assigned).`}
              {addResult.conflicts.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Conflicts:
                  </Typography>
                  {addResult.conflicts.map((c) => (
                    <Typography key={c.zip} variant="body2">
                      {c.zip} ({c.city}) — already in {c.existing_region}
                    </Typography>
                  ))}
                </Box>
              )}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Auto-fill all zip codes for {regionState}:
            </Typography>
            <Button
              a11yLabel={`Add all ${regionState} zip codes`}
              label={`Add all ${regionState} zip codes`}
              isLoading={addLoading}
              onClick={handleStateFill}
            />
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Or add specific zip codes (comma or space separated):
            </Typography>
            <TextField
              size="small"
              multiline
              rows={3}
              placeholder="15213, 15217, 15232"
              value={manualZips}
              onChange={(e) => setManualZips(e.target.value)}
              fullWidth
              sx={{ mb: 1 }}
            />
            <Button
              a11yLabel="Add zip codes"
              label="Add Zip Codes"
              isLoading={addLoading}
              onClick={handleManualAdd}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ZipcodeManager;
